import React, { useState } from 'react';
import { X } from 'lucide-react';
import { TopicMultiSelect } from '@/components/shared/TopicMultiSelect';

interface FlashcardSettingsModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (settings: FlashcardSettings) => void;
  availableTopics?: string[];
}

export interface FlashcardSettings {
  count: number;
  topics: string[];
  instructions: string;
}

export const FlashcardSettingsModal: React.FC<FlashcardSettingsModalProps> = ({
  open,
  onClose,
  onSubmit,
  availableTopics = [],
}) => {
  const [flashcardsCount, setFlashcardsCount] = useState('');
  const [instructions, setInstructions] = useState('');
  const [selectedTopics, setSelectedTopics] = useState<string[]>([]);

  if (!open) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      count: parseInt(flashcardsCount) || 10,
      topics: selectedTopics.length > 0 ? selectedTopics : ['All Topics'],
      instructions
    });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-x-auto">
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm animate-in fade-in-0"
        onClick={onClose} />

      <div
        role="dialog"
        aria-labelledby="flashcard-dialog-title"
        aria-describedby="flashcard-dialog-description"
        className="relative z-50 mx-1 my-1 sm:mx-6 sm:my-16 w-full max-w-[520px] rounded-xl border border-border bg-background p-6 shadow-lg animate-in fade-in-0 zoom-in-95">

        <button
          type="button"
          onClick={onClose}
          className="absolute right-4 top-4 rounded-lg p-1 text-muted-foreground opacity-70 transition-opacity hover:opacity-100 hover:text-foreground">
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </button>

        <div className="flex flex-col space-y-1.5 text-center sm:text-left">
          <h2 id="flashcard-dialog-title" className="text-lg leading-none tracking-tight text-foreground">
            Create Flashcard Set
          </h2>
          <p id="flashcard-dialog-description" className="text-sm text-muted-foreground">
            Select specific concepts and customize your flashcard set
          </p>
        </div>

        <form onSubmit={handleSubmit} className="mt-6 space-y-6">
          <div className="space-y-4">
            {/* Number of flashcards */}
            <div className="space-y-2">
              <label htmlFor="flashcards-count" className="text-sm font-medium leading-none text-foreground">
                Number of flashcards<span className="ml-1 text-destructive">*</span>
              </label>
              <input
                id="flashcards-count"
                type="number"
                min="1"
                placeholder="e.g., 10"
                value={flashcardsCount}
                onChange={(e) => setFlashcardsCount(e.target.value)}
                className="flex h-10 w-full rounded-lg border border-input bg-background px-4 py-6 text-sm ring-offset-background transition-all duration-200 placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring focus-visible:ring-offset-2 text-foreground" />
            </div>

            {/* Select topics */}
            <div className="space-y-2">
              <label className="text-sm font-medium leading-none text-foreground">
                Topics
                {selectedTopics.length > 0 && (
                  <span className="ml-2 text-xs text-muted-foreground">
                    ({selectedTopics.length} selected)
                  </span>
                )}
              </label>
              <TopicMultiSelect
                availableTopics={availableTopics}
                selectedTopics={selectedTopics}
                onChange={setSelectedTopics}
                placeholder="Optional: Select concepts to focus on"
              />
            </div>

            {/* Instructions */}
            <div className="space-y-2">
              <label htmlFor="flashcard-instructions" className="text-sm font-medium leading-none text-foreground">
                What should the flashcard focus on?
              </label>
              <textarea
                id="flashcard-instructions"
                placeholder="Focus on the parts that are about..."
                value={instructions}
                onChange={(e) => setInstructions(e.target.value)}
                className="flex max-h-[80px] min-h-[45px] w-full rounded-xl border border-input bg-background text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring resize-none p-3 text-foreground" />
            </div>
          </div>

          <div className="flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2">
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center justify-center whitespace-nowrap rounded-lg text-sm font-medium transition-colors border border-input bg-background hover:bg-accent hover:text-accent-foreground h-10 px-4 py-2 text-foreground">
              Cancel
            </button>
            <button
              type="submit"
              className="inline-flex items-center justify-center whitespace-nowrap rounded-lg text-sm font-medium transition-colors bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-4 py-2">
              Create Set
            </button>
          </div>
        </form>
      </div>
    </div>);
};
